import React, { useEffect, useRef, useState } from 'react';
import { X, Check, LocateFixed } from 'lucide-react';
import { createStreetMap } from './streetMap.jsx';
import './sitePinPicker.scss';

// انتخابِ «لوکیشنِ دقیقِ ساختمان» روی نقشه‌ی خودِ سرور (خیابان‌ها و ساختمان‌ها از فایلِ /geo — بدونِ اینترنتِ خارجی).
// نقطه‌ی ثابتِ وسطِ صفحه (پین) است و کاربر نقشه را زیرش جابه‌جا می‌کند. تأیید وقتی فعال است که به‌قدرِ کافی زوم شده باشد.
// نکته: داده‌ی خیابان‌ها/ساختمان‌ها تا زوم ۱۵ ساخته شده؛ زوم‌های بالاتر همان داده را بزرگ‌نمایی می‌کنند.

const MIN_CONFIRM_ZOOM = 17;
const DEFAULT_CENTER = [35.6892, 51.389]; // [lat, lng] — تهران
const DEFAULT_ZOOM = 11;

// center: مرکزِ استانِ انتخاب‌شده ([lat, lng]) — نقشه از همان‌جا شروع می‌شود
function SitePinPicker({ initial, center, onClose, onConfirm }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(initial ? 18 : DEFAULT_ZOOM);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');

  useEffect(() => {
    const start = initial ? [initial.lat, initial.lng] : center || DEFAULT_CENTER;
    const map = createStreetMap(mapContainerRef.current, {
      center: [start[1], start[0]], // MapLibre [lng, lat]
      zoom: initial ? 18 : DEFAULT_ZOOM,
    });
    mapRef.current = map;
    map.on('zoomend', () => setZoom(map.getZoom()));
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setLocateError('مرورگر اجازه‌ی پیدا کردنِ موقعیت را نمی‌دهد');
      return;
    }
    setLocating(true);
    setLocateError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 18 });
      },
      () => {
        setLocating(false);
        setLocateError('موقعیتِ شما پیدا نشد — روی نقشه دستی پیدا کنید');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const canConfirm = zoom >= MIN_CONFIRM_ZOOM;

  const handleConfirm = () => {
    if (!canConfirm || !mapRef.current) return;
    const c = mapRef.current.getCenter(); // {lng, lat}
    onConfirm?.({ lat: Number(c.lat.toFixed(6)), lng: Number(c.lng.toFixed(6)) });
    onClose?.();
  };

  return (
    <div className="site-pin-picker">
      <div className="site-pin-picker__header">
        <button type="button" className="site-pin-picker__icon-btn" onClick={onClose} aria-label="بستن">
          <X size={20} strokeWidth={1.8} />
        </button>
        <span className="site-pin-picker__title">لوکیشنِ دقیقِ ساختمان</span>
        <button
          type="button"
          className="site-pin-picker__icon-btn"
          onClick={handleLocate}
          disabled={locating}
          aria-label="موقعیتِ من"
        >
          <LocateFixed size={20} strokeWidth={1.8} />
        </button>
      </div>

      <div className="site-pin-picker__map-wrap">
        <div className="site-pin-picker__map" ref={mapContainerRef} />
        {/* پینِ ثابتِ وسطِ نقشه — نوکِ پین دقیقاً مرکزِ نقشه است */}
        <div className="site-pin-picker__pin" aria-hidden="true">
          <svg width="36" height="48" viewBox="0 0 36 48">
            <path
              d="M18 47C18 47 34 28.5 34 17.5C34 8.4 26.8 1 18 1C9.2 1 2 8.4 2 17.5C2 28.5 18 47 18 47Z"
              fill="#000000"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <circle cx="18" cy="17.5" r="5.5" fill="#ffffff" />
          </svg>
        </div>
      </div>

      <div className="site-pin-picker__footer">
        <p className="site-pin-picker__hint">
          {canConfirm
            ? 'نقشه را طوری جابه‌جا کنید که نوکِ پین دقیقاً روی خودِ ساختمان باشد.'
            : 'برای دقت، روی نقشه نزدیک‌تر شوید (زوم کنید) تا ساختمان‌ها دیده شوند.'}
        </p>
        {locateError && <p className="site-pin-picker__error">{locateError}</p>}
        <button type="button" className="site-pin-picker__confirm" onClick={handleConfirm} disabled={!canConfirm}>
          <Check size={16} strokeWidth={2.2} />
          <span>تأییدِ لوکیشن</span>
        </button>
      </div>
    </div>
  );
}

export default SitePinPicker;
